import { graphql } from '~/graphql';
import { graphqlQueryRetry, useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { extractGraphQLErrorCodes, type ClientError } from '~/utils';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import type {
  RequestForQuoteArchivedFilter,
  RequestForQuoteFiltersInput,
} from '~/graphql/graphql';
import type {
  PaginatedFetchOptions,
  RouteSort,
} from '~/hooks/usePaginatedQuery';
import { BUY_LISTS_QUERY_KEY, BUY_LIST_DETAIL_QUERY_KEY } from '~/api/buyList';
import { NO_LONGER_AVAILABLE_ERROR_CODE } from '~/lib/magicLinkToken';

export const quoteChangeQueryKeys = () => [
  'quote',
  'requestForQuote',
  'requestForQuotes',
  'myRequestForQuotes',
  'myQuotes',
  BUY_LISTS_QUERY_KEY,
  BUY_LIST_DETAIL_QUERY_KEY,
];

const archiveQueryKeys = () => [
  ...quoteChangeQueryKeys(),
  'myRequestForQuoteFilterOptions',
];

export const quoteTimelineRoundFragment = graphql(`
  fragment QuoteTimelineRound on QuoteRound {
    roundNumber
    party
    authorName
    declined
    declineReason
    createdAt
    pickupPriceForTally {
      amount
      currency
    }
    deliveryPriceForTally {
      amount
      currency
    }
    items {
      pickupPrice {
        amount
        currency
      }
      deliveryPrice {
        amount
        currency
      }
      platformProductsLength {
        lengthDisplayValue
      }
    }
  }
`);

export const quoteOfferRoundFragment = graphql(`
  fragment QuoteOfferRound on QuoteRound {
    roundNumber
    party
    pickupPriceForTally {
      amount
      currency
    }
    deliveryPriceForTally {
      amount
      currency
    }
    items {
      quantityInPieces
      pickupPrice {
        amount
        currency
      }
      deliveryPrice {
        amount
        currency
      }
      platformProductsLength {
        id
      }
      piecesPerBundle
    }
  }
`);

export const useGetRequestForQuotes = () => {
  const query = graphql(`
    query RequestForQuotes {
      requestForQuotes {
        id
        status
        createdAt
        tallyPlatformProduct {
          id
          name
        }
        items {
          id
          platformProductsLength {
            platformProduct {
              id
              name
            }
          }
        }
      }
    }
  `);

  const { data, isLoading, isError } = useGraphqlQuery(query, [
    'requestForQuotes',
  ]);

  return {
    requestForQuotes: data?.requestForQuotes ?? [],
    isLoading,
    isError,
  };
};

export const useGetDraftRequestForQuote = (
  tallyPlatformProductId: string | undefined,
  shippingLocationId: string | undefined
) => {
  const query = graphql(`
    query DraftRequestForQuote(
      $tallyPlatformProductId: ID!
      $shippingLocationId: ID!
    ) {
      draftRequestForQuote(
        tallyPlatformProductId: $tallyPlatformProductId
        shippingLocationId: $shippingLocationId
      ) {
        id
        leadTimeDays
        useTallyPrice
        pickupPriceForTally {
          amount
          currency
        }
        deliveryPriceForTally {
          amount
          currency
        }
        items {
          id
          quantityInPieces
          pickupPrice {
            amount
            currency
          }
          deliveryPrice {
            amount
            currency
          }
          platformProductsLength {
            id
            length {
              id
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'draftRequestForQuote',
      tallyPlatformProductId ?? '',
      shippingLocationId ?? '',
    ],
    {
      tallyPlatformProductId: tallyPlatformProductId ?? '',
      shippingLocationId: shippingLocationId ?? '',
    },
    { enabled: !!tallyPlatformProductId && !!shippingLocationId }
  );
};

export const useGetRequestForQuoteById = (id: string | undefined) => {
  const query = graphql(`
    query GetRequestForQuoteWithQuotes($id: ID!) {
      requestForQuote(id: $id) {
        id
        status
        createdAt
        createdByUserName
        buyList {
          id
        }
        leadTimeDays
        useTallyPrice
        cannedTally {
          id
          name
        }
        cannedTallyCount
        currency
        tallyPlatformProduct {
          id
          name
          standardUnitsCode
          piecesPerBundle {
            value
            unit
          }
        }
        shippingLocation {
          id
          name
          code
          facility {
            id
            address {
              addressLine1
              addressLine2
              city
              region
              zipCode
              country
            }
          }
        }
        pickupPriceForTally {
          amount
          currency
        }
        deliveryPriceForTally {
          amount
          currency
        }
        items {
          id
          quantityInPieces
          piecesPerBundle
          pickupPrice {
            amount
            currency
          }
          deliveryPrice {
            amount
            currency
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              code
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
        }
      }
      quotesForRequestForQuote(requestForQuoteId: $id) {
        id
        status
        negotiationAwaitingParty
        primaryContact {
          id
          ...PrimaryContact
        }
        recipients {
          id
          firstName
          lastName
          phoneNumber
          externalId
        }
        lockedRecipientIds
        rounds {
          ...QuoteTimelineRound
          ...QuoteOfferRound
        }
        currency
        leadTimeDays
        submittedAt
        requestForQuote {
          id
          status
        }
        sellingOrganization {
          id
          name
          sellingOrganizationCode
          facilities {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
            }
            locations {
              id
              code
            }
          }
        }
        sellingOrganizationFacility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
          }
        }
        offeredPickupPriceForTally {
          amount
          currency
        }
        offeredDeliveryPriceForTally {
          amount
          currency
        }
        items {
          id
          quantityInPieces
          piecesPerBundle
          status
          offeredPickupPrice {
            amount
            currency
          }
          offeredDeliveryPrice {
            amount
            currency
          }
          order {
            id
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              code
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['requestForQuote', id ?? ''],
    { id: id ?? '' },
    { enabled: !!id }
  );
};

export const useGetRequestForQuoteFullyPriced = (id: string | undefined) => {
  const query = graphql(`
    query GetRequestForQuoteFullyPriced($id: ID!) {
      requestForQuote(id: $id) {
        id
        fullyPriced
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['requestForQuote', id ?? '', 'fullyPriced'],
    { id: id ?? '' },
    { enabled: !!id }
  );
};

export const useGetQuoteById = (id: string | undefined) => {
  const query = graphql(`
    query GetQuoteById($id: ID!) {
      quote(id: $id) {
        id
        status
        negotiationAwaitingParty
        rounds {
          ...QuoteTimelineRound
        }
        currency
        leadTimeDays
        sellingOrganization {
          id
          name
          facilities {
            id
            code
            address {
              addressLine1
              addressLine2
              city
              region
            }
            locations {
              id
              code
            }
          }
        }
        sellingOrganizationFacility {
          id
          code
          address {
            addressLine1
            addressLine2
            city
            region
          }
        }
        offeredPickupPriceForTally {
          amount
          currency
        }
        offeredDeliveryPriceForTally {
          amount
          currency
        }
        items {
          id
          quantityInPieces
          piecesPerBundle
          status
          order {
            id
          }
          offeredPickupPrice {
            amount
            currency
          }
          offeredDeliveryPrice {
            amount
            currency
          }
          platformProductsLength {
            id
            lengthDisplayValue
            isNominal
            length {
              id
              displayValue
              actualLengthInches
              nominalLengthInches
            }
            platformProduct {
              id
              name
              standardUnitsCode
              nominalWidthInches
              actualWidthInches
              nominalThicknessInches
              actualThicknessInches
              actualLengthInches
              piecesPerBundle {
                value
                unit
              }
            }
          }
        }
        requestForQuote {
          id
          status
          createdAt
          leadTimeDays
          useTallyPrice
          cannedTally {
            id
            name
          }
          cannedTallyCount
          currency
          buyingOrganization {
            id
            name
          }
          createdByUserName
          createdByUser {
            id
            firstName
            lastName
            phoneNumber
            externalId
          }
          transportVehicle {
            id
            name
          }
          tallyPlatformProduct {
            id
            name
            standardUnitsCode
            piecesPerBundle {
              value
              unit
            }
          }
          shippingLocation {
            id
            name
            code
            facility {
              id
              address {
                addressLine1
                addressLine2
                city
                region
                zipCode
                country
              }
            }
          }
          pickupPriceForTally {
            amount
            currency
          }
          deliveryPriceForTally {
            amount
            currency
          }
          items {
            id
            quantityInPieces
            piecesPerBundle
            pickupPrice {
              amount
              currency
            }
            deliveryPrice {
              amount
              currency
            }
            platformProductsLength {
              id
              lengthDisplayValue
              isNominal
              length {
                id
                displayValue
                actualLengthInches
                nominalLengthInches
              }
              platformProduct {
                id
                name
                standardUnitsCode
                nominalWidthInches
                actualWidthInches
                nominalThicknessInches
                actualThicknessInches
                actualLengthInches
                piecesPerBundle {
                  value
                  unit
                }
              }
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['quote', id ?? ''],
    { id: id ?? '' },
    {
      enabled: !!id,
      retry: (failureCount, error) =>
        !isQuoteNoLongerAvailableError(error) &&
        graphqlQueryRetry(failureCount, error),
    }
  );
};

export function isQuoteNoLongerAvailableError(error: unknown): boolean {
  return extractGraphQLErrorCodes(error as ClientError).includes(
    NO_LONGER_AVAILABLE_ERROR_CODE
  );
}

export const useGetMyQuotes = (
  page: number,
  size: number,
  needsResponse?: boolean,
  sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query MyQuotes(
      $page: Int!
      $size: Int!
      $needsResponse: Boolean
      $sort: [SortInput!]
    ) {
      myQuotes(
        pageInput: { page: $page, size: $size }
        needsResponse: $needsResponse
        sort: $sort
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          status
          needsSellerResponse
          createdAt
          leadTimeDays
          offeredPickupPriceForTally {
            amount
            currency
          }
          offeredDeliveryPriceForTally {
            amount
            currency
          }
          requestForQuote {
            id
            status
            tallyPlatformProduct {
              id
              name
            }
            items {
              id
              platformProductsLength {
                platformProduct {
                  id
                  name
                }
              }
            }
            buyingOrganization {
              id
              name
            }
          }
          items {
            id
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['myQuotes', page, size, needsResponse, sort ?? null],
    { page, size, needsResponse, sort },
    options
  );
};

export type MyRequestForQuotesParams = {
  needsResponse?: boolean;
  archived?: RequestForQuoteArchivedFilter;
  filters?: RequestForQuoteFiltersInput;
};

export const useGetMyRequestForQuotes = (
  page: number,
  size: number,
  params?: MyRequestForQuotesParams,
  _sort?: RouteSort,
  options?: PaginatedFetchOptions
) => {
  const query = graphql(`
    query MyRequestForQuotes(
      $page: Int!
      $size: Int!
      $needsResponse: Boolean
      $archived: RequestForQuoteArchivedFilter
      $filters: RequestForQuoteFiltersInput
    ) {
      myRequestForQuotes(
        pageInput: { page: $page, size: $size }
        needsResponse: $needsResponse
        archived: $archived
        filters: $filters
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          status
          createdAt
          createdByUserName
          tallyPlatformProduct {
            id
            name
          }
          items {
            id
            platformProductsLength {
              platformProduct {
                id
                name
              }
            }
          }
          quotes {
            id
            sellingOrganization {
              id
              name
              sellingOrganizationCode
            }
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    [
      'myRequestForQuotes',
      page,
      size,
      params?.needsResponse ?? null,
      params?.archived ?? null,
      params?.filters ?? null,
    ],
    {
      page,
      size,
      needsResponse: params?.needsResponse,
      archived: params?.archived,
      filters: params?.filters,
    },
    options
  );
};

const myRequestForQuoteFilterOptionsQuery = graphql(`
  query MyRequestForQuoteFilterOptions {
    myRequestForQuoteFilterOptions {
      products {
        id
        name
        code
      }
      sellers {
        id
        name
        sellingOrganizationCode
      }
      creators {
        id
        name
      }
      hasArchivedRequests
      hasSentRequests
    }
  }
`);

export const useGetMyRequestForQuoteFilterOptions = () =>
  useGraphqlQuery(myRequestForQuoteFilterOptionsQuery, [
    'myRequestForQuoteFilterOptions',
  ]);

const upsertRequestForQuoteMutation = graphql(`
  mutation upsertRequestForQuote(
    $tallyPlatformProductId: ID
    $leadTimeDays: Int
    $shippingLocationId: ID
    $pickupPriceForTally: MoneyInput
    $deliveryPriceForTally: MoneyInput
    $useTallyPrice: Boolean
  ) {
    upsertRequestForQuote(
      tallyPlatformProductId: $tallyPlatformProductId
      leadTimeDays: $leadTimeDays
      shippingLocationId: $shippingLocationId
      pickupPriceForTally: $pickupPriceForTally
      deliveryPriceForTally: $deliveryPriceForTally
      useTallyPrice: $useTallyPrice
    ) {
      id
      leadTimeDays
      useTallyPrice
      shippingLocation {
        id
      }
    }
  }
`);

export const useUpsertRequestForQuote = () =>
  useGraphqlMutation(upsertRequestForQuoteMutation, [
    'requestForQuotes',
    'requestForQuote',
    'draftRequestForQuote',
  ]);

const addRequestForQuoteItemsMutation = graphql(`
  mutation addRequestForQuoteItems(
    $requestForQuoteId: ID!
    $items: [RequestForQuoteItemInput!]!
    $replace: Boolean
  ) {
    addRequestForQuoteItems(
      requestForQuoteId: $requestForQuoteId
      items: $items
      replace: $replace
    ) {
      id
    }
  }
`);

export const useAddRequestForQuoteItems = () =>
  useGraphqlMutation(addRequestForQuoteItemsMutation, [
    'draftRequestForQuote',
    'requestForQuote',
    'requestForQuotes',
  ]);

const submitRequestForQuoteMutation = graphql(`
  mutation submitRequestForQuote(
    $id: ID!
    $sellerInvitations: [SellerInvitationInput!]!
  ) {
    submitRequestForQuote(id: $id, sellerInvitations: $sellerInvitations) {
      id
      status
    }
  }
`);

export const useSubmitRequestForQuote = () =>
  useGraphqlMutation(submitRequestForQuoteMutation, [
    'requestForQuotes',
    'requestForQuote',
    'draftRequestForQuote',
    'myRequestForQuotes',
    'myRequestForQuoteFilterOptions',
  ]);

const createRequestForQuoteFromBuyListMutation = graphql(`
  mutation createRequestForQuoteFromBuyList(
    $buyListId: ID!
    $items: [BuyListItemSelectionInput!]!
    $tallies: [BuyListTallySelectionInput!]! = []
    $sellerInvitations: [SellerInvitationInput!]!
  ) {
    createRequestForQuoteFromBuyList(
      buyListId: $buyListId
      items: $items
      tallies: $tallies
      sellerInvitations: $sellerInvitations
    ) {
      id
      status
    }
  }
`);

export const useCreateRequestForQuoteFromBuyList = () =>
  useGraphqlMutation(createRequestForQuoteFromBuyListMutation, [
    BUY_LISTS_QUERY_KEY,
    BUY_LIST_DETAIL_QUERY_KEY,
    'requestForQuotes',
    'myRequestForQuotes',
    'myRequestForQuoteFilterOptions',
  ]);

const createRequestForQuoteMutation = graphql(`
  mutation createRequestForQuote(
    $shippingLocationId: ID!
    $currency: Currency!
    $leadTimeDays: Int
    $items: [CreateRequestForQuoteItemInput!]!
    $sellerInvitations: [SellerInvitationInput!]!
  ) {
    createRequestForQuote(
      shippingLocationId: $shippingLocationId
      currency: $currency
      leadTimeDays: $leadTimeDays
      items: $items
      sellerInvitations: $sellerInvitations
    ) {
      id
    }
  }
`);

export const useCreateRequestForQuote = () =>
  useGraphqlMutation(createRequestForQuoteMutation, [
    'requestForQuotes',
    'myRequestForQuotes',
    'myRequestForQuoteFilterOptions',
  ]);

// A function, not a constant: tests that mock ~/api/buyList would otherwise need
// BUY_LISTS_QUERY_KEY when this module loads.
const orderCreatingQueryKeys = () => [
  'requestForQuote',
  'requestForQuotes',
  'myRequestForQuotes',
  'orders',
  'orderCounts',
  'myQuotes',
  'carts',
  BUY_LISTS_QUERY_KEY,
  BUY_LIST_DETAIL_QUERY_KEY,
];

const acceptQuoteMutation = graphql(`
  mutation acceptQuote($id: ID!) {
    acceptQuote(id: $id) {
      id
      status
      order {
        id
      }
    }
  }
`);

export const useAcceptQuote = () =>
  useGraphqlMutation(acceptQuoteMutation, orderCreatingQueryKeys());

const acceptQuoteItemsMutation = graphql(`
  mutation acceptQuoteItems($quoteItemIds: [ID!]!) {
    acceptQuoteItems(quoteItemIds: $quoteItemIds) {
      id
    }
  }
`);

export const useAcceptQuoteItems = () =>
  useGraphqlMutation(acceptQuoteItemsMutation, orderCreatingQueryKeys());

const submitQuoteItemEditsMutation = graphql(`
  mutation submitQuoteItemEdits(
    $quoteId: ID!
    $input: SubmitQuoteItemEditsInput!
  ) {
    submitQuoteItemEdits(quoteId: $quoteId, input: $input) {
      id
      status
      items {
        id
        status
        quantityInPieces
        piecesPerBundle
        offeredPickupPrice {
          amount
          currency
        }
        offeredDeliveryPrice {
          amount
          currency
        }
        platformProductsLength {
          id
          lengthDisplayValue
          length {
            id
            displayValue
            actualLengthInches
          }
        }
      }
    }
  }
`);

export const useSubmitQuoteItemEdits = () =>
  useGraphqlMutation(submitQuoteItemEditsMutation, quoteChangeQueryKeys());

const counterQuoteMutation = graphql(`
  mutation counterQuote($quoteId: ID!, $input: SubmitQuoteItemEditsInput!) {
    counterQuote(quoteId: $quoteId, input: $input) {
      id
      status
    }
  }
`);

export const useCounterQuote = () =>
  useGraphqlMutation(counterQuoteMutation, quoteChangeQueryKeys());

const acceptCounterQuoteMutation = graphql(`
  mutation acceptCounterQuote($quoteId: ID!) {
    acceptCounterQuote(quoteId: $quoteId) {
      id
      status
    }
  }
`);

export const useAcceptCounterQuote = () =>
  useGraphqlMutation(acceptCounterQuoteMutation, quoteChangeQueryKeys());

const declineQuoteMutation = graphql(`
  mutation declineQuote($quoteId: ID!, $reason: String) {
    declineQuote(quoteId: $quoteId, reason: $reason) {
      id
      status
    }
  }
`);

export const useDeclineQuote = () =>
  useGraphqlMutation(declineQuoteMutation, quoteChangeQueryKeys());

const archiveQuoteMutation = graphql(`
  mutation archiveQuote($quoteId: ID!) {
    archiveQuote(quoteId: $quoteId) {
      id
      status
    }
  }
`);

export const useArchiveQuote = () =>
  useGraphqlMutation(archiveQuoteMutation, archiveQueryKeys(), undefined, {
    invalidateOnError: true,
  });

const archiveRequestForQuoteMutation = graphql(`
  mutation archiveRequestForQuote($id: ID!) {
    archiveRequestForQuote(id: $id) {
      id
      status
    }
  }
`);

export const useArchiveRequestForQuote = () =>
  useGraphqlMutation(archiveRequestForQuoteMutation, archiveQueryKeys());

const fulfillRequestForQuoteMutation = graphql(`
  mutation fulfillRequestForQuote($id: ID!) {
    fulfillRequestForQuote(id: $id) {
      id
      status
    }
  }
`);

export const useFulfillRequestForQuote = () =>
  useGraphqlMutation(fulfillRequestForQuoteMutation, quoteChangeQueryKeys());

export const useGetQuoteComments = (quoteId: string | undefined) => {
  const query = graphql(`
    query QuoteComments($quoteId: ID!) {
      quoteComments(quoteId: $quoteId) {
        id
        body
        authorName
        createdAt
        authorOrganization {
          id
          name
          sellingOrganizationCode
        }
      }
    }
  `);

  return useGraphqlQuery(
    query,
    ['quoteComments', quoteId ?? ''],
    { quoteId: quoteId ?? '' },
    { enabled: !!quoteId }
  );
};

const postQuoteCommentMutation = graphql(`
  mutation postQuoteComment($quoteId: ID!, $body: String!) {
    postQuoteComment(quoteId: $quoteId, body: $body) {
      id
    }
  }
`);

export const usePostQuoteComment = () =>
  useGraphqlMutation(postQuoteCommentMutation, ['quoteComments']);
